import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Avatar, Box, Chip, Container, IconButton, InputAdornment,
  LinearProgress, MenuItem, TextField, Toolbar, Tooltip, Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import Public from '@mui/icons-material/Public';
import Refresh from '@mui/icons-material/Refresh';
import Search from '@mui/icons-material/Search';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import DataTable, { DataTableColumn } from '../../design/DataTable';
import MarketStatusChip from '../../design/MarketStatusChip';
import { appBarGradient, accentColors } from '../../design/theme';
import { StockRow, StocksResponse } from '../../hooks/useUniverse';

const ITEMS_PER_PAGE = 25;
const ALL = 'All';

const COLUMNS: DataTableColumn<StockRow>[] = [
  {
    key: 'Ticker', label: 'Ticker', sortable: true, minWidth: 90,
    render: (s) => (
      <Typography variant="body2" component="span" sx={{ fontWeight: 700, color: accentColors.ticker }}>
        {s.Ticker}
      </Typography>
    ),
  },
  { key: 'Company', label: 'Company', sortable: true, minWidth: 220, render: (s) => s.Company ?? '—' },
  { key: 'Sector', label: 'Sector', sortable: true, minWidth: 140, render: (s) => s.Sector ?? '—' },
  { key: 'Industry', label: 'Industry', sortable: true, minWidth: 180, render: (s) => s.Industry ?? '—' },
  { key: 'Country', label: 'Country', sortable: true, minWidth: 100, render: (s) => s.Country ?? '—' },
  {
    key: 'BlackList', label: 'Status', sortable: true, minWidth: 100,
    render: (s) => (s.BlackList
      ? <Chip label="Delisted" color="error" size="small" variant="outlined" />
      : <Chip label="Active" color="success" size="small" variant="outlined" />),
  },
];

const Universe: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [rows, setRows] = useState<StockRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sectors, setSectors] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<string>('Ticker');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sector, setSector] = useState(ALL);
  const [industry, setIndustry] = useState(ALL);
  const [status, setStatus] = useState(ALL); // All | Active | Delisted
  const fetchSeq = useRef(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchStocks = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: String((page - 1) * ITEMS_PER_PAGE),
        limit: String(ITEMS_PER_PAGE),
        sort_by: orderBy,
        sort_order: order,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (sector !== ALL) params.set('sector', sector);
      if (industry !== ALL) params.set('industry', industry);
      if (status !== ALL) params.set('blacklist', String(status === 'Delisted'));
      const response = await axios.get(`/stocks?${params.toString()}`);
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      const data: StocksResponse = response.data;
      setRows(data.stocks);
      setTotalCount(data.total);
      setTotalPages(data.total_pages);
      setSectors(data.sectors);
      setIndustries(data.industries);
      setError(null);
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      console.error('Error fetching stocks:', e);
      setError('Failed to fetch stocks');
    } finally {
      if (seq === fetchSeq.current) {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [page, orderBy, order, debouncedSearch, sector, industry, status]);

  useEffect(() => { fetchStocks(); }, [fetchStocks]);

  // Clamp the page when the total shrinks under the user (e.g. a refetch
  // returns fewer pages); the fetch effect then refetches the clamped page.
  useEffect(() => {
    if (!loading && totalPages >= 1 && page > totalPages) setPage(totalPages);
  }, [loading, totalPages, page]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSort = (key: string) => {
    if (orderBy === key) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(key); setOrder('asc'); }
    setPage(1);
  };

  const filterSelect = (label: string, value: string, options: string[], onChange: (v: string) => void) => (
    <TextField
      select size="small" label={label} value={value} sx={{ minWidth: 160 }}
      onChange={(e) => { onChange(e.target.value); setPage(1); }}
    >
      <MenuItem value={ALL}>{ALL}</MenuItem>
      {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </TextField>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0} sx={{ background: appBarGradient }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/')} aria-label="Back to home" sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <IconButton onClick={() => navigate('/')} aria-label="Home" sx={{ p: 0, mr: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}><Public /></Avatar>
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>Stock Universe</Typography>
          <Box sx={{ mr: 1 }}>
            <MarketStatusChip />
          </Box>
          <IconButton color="inherit" onClick={handleLogout} aria-label="Log out">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            size="small" placeholder="Search ticker or company" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)} sx={{ minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          {filterSelect('Sector', sector, sectors, setSector)}
          {filterSelect('Industry', industry, industries, setIndustry)}
          {filterSelect('Status', status, ['Active', 'Delisted'], setStatus)}
          {hasLoadedOnce && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {totalCount} stocks
            </Typography>
          )}
        </Box>

        {error ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography color="error">{error}</Typography>
            <Tooltip title="Refresh stocks">
              <IconButton onClick={() => fetchStocks()} color="primary" aria-label="Retry">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <>
            {loading && hasLoadedOnce && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
            <DataTable
              columns={COLUMNS} rows={rows} rowKey={(s) => s.Id} loading={loading && !hasLoadedOnce}
              skeletonRows={ITEMS_PER_PAGE}
              emptyMessage="No stocks match the current filters"
              sortBy={orderBy} sortDir={order} onSortChange={handleSort}
              onRowClick={(s) => navigate(`/universe/${s.Ticker}`)}
              rowAriaLabel={(s) => `View ${s.Ticker} details`}
              page={page} pageCount={totalPages} onPageChange={setPage}
            />
          </>
        )}
      </Container>
    </Box>
  );
};

export default Universe;
